import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { useQuery } from "@tanstack/react-query";
import { A1X_SOLANA_TOKEN, A1X_SOLANA_DECIMALS } from "@/config/sharedConfig";

export const useSolanaTokenBalance = () => {
  const { connection } = useConnection();
  const { publicKey } = useWallet();

  return useQuery({
    queryKey: ["solana-a1x-balance", publicKey?.toBase58()],
    queryFn: async () => {
      if (!publicKey) return null;

      try {
        const tokenMint = new PublicKey(A1X_SOLANA_TOKEN);

        // Get the associated token account address
        const tokenAccounts = await connection.getTokenAccountsByOwner(
          publicKey,
          {
            mint: tokenMint,
          }
        );

        const tokenAccount = tokenAccounts.value[0];

        if (!tokenAccount) {
          // Token account doesn't exist yet, return 0 balance
          return {
            balance: "0",
            decimals: A1X_SOLANA_DECIMALS,
            uiAmount: 0,
            formattedBalance: "0",
          };
        }

        // Get the token account balance
        const tokenAccountInfo = await connection.getTokenAccountBalance(
          tokenAccount.pubkey
        );

        const resp = {
          balance: tokenAccountInfo.value.amount,
          decimals: tokenAccountInfo.value.decimals,
          uiAmount: tokenAccountInfo.value.uiAmount || 0,
          formattedBalance: tokenAccountInfo.value.uiAmountString || "0",
        };

        return resp;
      } catch (error) {
        // Log error but don't throw - return 0 balance
        console.error("Error fetching solana balance:", error);
        return {
          balance: "0",
          decimals: A1X_SOLANA_DECIMALS,
          uiAmount: 0,
          formattedBalance: "0",
        };
      }
    },
    enabled: !!publicKey && !!connection,
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};
